package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportPayload
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportPreview
import br.com.vvrefeicoes.portal.domain.model.PurchasesImportResult
import br.com.vvrefeicoes.portal.domain.model.PurchasesProductRow
import br.com.vvrefeicoes.portal.domain.model.PurchasesProvision
import br.com.vvrefeicoes.portal.domain.model.PurchasesSummaryLine
import br.com.vvrefeicoes.portal.domain.model.PurchasesType
import br.com.vvrefeicoes.portal.domain.repository.PurchasesRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope

class PurchasesEntriesController(
    private val repository: PurchasesRepository,
) {
    var summary by mutableStateOf<List<PurchasesSummaryLine>>(emptyList())
        private set

    var summaryLoading by mutableStateOf(false)
        private set

    var rowsByType by mutableStateOf<Map<PurchasesType, List<PurchasesProductRow>>>(emptyMap())
        private set

    var loadingTypes by mutableStateOf<Set<PurchasesType>>(emptySet())
        private set

    var error by mutableStateOf<String?>(null)
        private set

    var provision by mutableStateOf<PurchasesProvision?>(null)
        private set

    var provisionSaving by mutableStateOf(false)
        private set

    var importPreview by mutableStateOf<PurchasesImportPreview?>(null)
        private set

    var importError by mutableStateOf<String?>(null)
        private set

    var previewing by mutableStateOf(false)
        private set

    var importing by mutableStateOf(false)
        private set

    private var importRequestId = 0
    private var rowsRequestId = 0

    suspend fun loadSummary(period: MonthPeriod) {
        summaryLoading = true
        try {
            summary = repository.summary(period)
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            error = exception.message ?: "Não foi possível carregar o resumo."
        } finally {
            summaryLoading = false
        }
    }

    suspend fun loadProvision(period: MonthPeriod) {
        provision = null
        try {
            provision = repository.provision(period)
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            error = exception.message ?: "Não foi possível carregar o provisionado."
        }
    }

    suspend fun saveProvision(
        period: MonthPeriod,
        amountCents: Long,
    ): Boolean {
        provisionSaving = true
        error = null
        return try {
            provision = repository.saveProvision(period, amountCents)
            true
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            error = exception.message ?: "Não foi possível salvar o provisionado."
            false
        } finally {
            provisionSaving = false
        }
    }

    suspend fun loadAllRows(period: MonthPeriod) {
        val requestId = ++rowsRequestId
        loadingTypes = PurchasesType.entries.toSet()
        try {
            val results =
                coroutineScope {
                    PurchasesType.entries
                        .map { type ->
                            async {
                                try {
                                    type to repository.load(type, period)
                                } catch (exception: CancellationException) {
                                    throw exception
                                } catch (exception: Throwable) {
                                    error = exception.message ?: "Não foi possível carregar os lançamentos."
                                    type to null
                                }
                            }
                        }.awaitAll()
                }
            if (requestId != rowsRequestId) return
            val loadedRows = results.mapNotNull { (type, rows) -> rows?.let { type to it } }
            rowsByType = rowsByType + loadedRows
        } finally {
            if (requestId == rowsRequestId) {
                loadingTypes = emptySet()
            }
        }
    }

    suspend fun loadRows(
        type: PurchasesType,
        period: MonthPeriod,
    ) {
        loadingTypes = loadingTypes + type
        try {
            rowsByType = rowsByType + (type to repository.load(type, period))
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            error = exception.message ?: "Não foi possível carregar os lançamentos."
        } finally {
            loadingTypes = loadingTypes - type
        }
    }

    fun rowsFor(type: PurchasesType): List<PurchasesProductRow> = rowsByType[type].orEmpty()

    fun isLoading(type: PurchasesType): Boolean = type in loadingTypes

    fun clearRowsCache() {
        rowsRequestId++
        rowsByType = emptyMap()
        loadingTypes = emptySet()
    }

    fun dismissError() {
        error = null
    }

    fun resetImport() {
        importRequestId++
        importPreview = null
        importError = null
        previewing = false
        importing = false
    }

    fun dismissImportError() {
        importError = null
    }

    suspend fun previewImport(payload: PurchasesImportPayload): PurchasesImportPreview? {
        val requestId = ++importRequestId
        previewing = true
        importPreview = null
        importError = null
        return try {
            val preview = repository.previewImport(payload)
            if (requestId == importRequestId) {
                importPreview = preview
                preview
            } else {
                null
            }
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            if (requestId == importRequestId) {
                importError = exception.message ?: "Não foi possível validar a planilha."
            }
            null
        } finally {
            if (requestId == importRequestId) {
                previewing = false
            }
        }
    }

    suspend fun importEntries(payload: PurchasesImportPayload): PurchasesImportResult? {
        val requestId = ++importRequestId
        importing = true
        importError = null
        return try {
            repository.importEntries(payload)
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            if (requestId == importRequestId) {
                importError = exception.message ?: "Não foi possível importar a planilha."
            }
            null
        } finally {
            if (requestId == importRequestId) {
                importing = false
            }
        }
    }

    suspend fun updateProduct(
        row: PurchasesProductRow,
    ): PurchasesProductRow? =
        try {
            repository.update(row)
        } catch (exception: CancellationException) {
            throw exception
        } catch (exception: Throwable) {
            error = exception.message ?: "Não foi possível salvar o lançamento."
            null
        }
}
